'use client';

import {
  type ChangeEvent,
  type FC,
  type FormEvent,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Button, ButtonGroup, Flex, Heading, Input } from '@/_components';
import { authenticateWithSorabanToken } from '@/_api/auth';
import { firmSelfSignUp } from '@/_api/resources';
import { LoginContext } from '@/_contexts/LoginProvider';
import { TemplateSignUpInContext } from '@/_contexts/TemplateSignUpInProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';

export const TemplateSignUp: FC = () => {
  const { setLoginState, username } = useContext(LoginContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { addToast } = useContext(ToastContext);
  const { signInWithWorkOS } = useContext(UserContext);
  const { firmGroupId, taxYear, template, templateHashId } = useContext(
    TemplateSignUpInContext,
  );

  const [fname, setFname] = useState<string>('');
  const [lname, setLname] = useState<string>('');

  const isValid = useMemo(() => {
    return Boolean(fname && lname);
  }, [fname, lname]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();

    try {
      setIsLoading(true);

      const { auth_token } = await firmSelfSignUp({
        email: username,
        firmGroupId,
        fname,
        lname,
        templateHashId,
        year: taxYear!,
      });

      // Authenticate with the Soraban token to get user data
      const { success, user } = await authenticateWithSorabanToken({
        sorabanToken: auth_token,
      });

      if (success && user) {
        // Use WorkOS authentication flow with both auth_token and user data
        await signInWithWorkOS({
          auth_token,
          user,
        });
      } else {
        throw new Error('Failed to authenticate with Soraban token');
      }
    } catch (error) {
      addToast('error', '', { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onFnameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFname(event.target.value);
  };

  const onLnameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLname(event.target.value);
  };

  return (
    <form onSubmit={onSubmit}>
      <Flex isColumn={true} gap={8} padding={10}>
        <Heading level={5}>{template?.name}</Heading>

        <Input
          onChange={() => {}}
          value={username}
          label="Email"
          placeholder="john@example.com"
          isDisabled
        />

        <Input
          value={fname}
          label="First name"
          onChange={onFnameChange}
          placeholder="John"
          isDisabled={isLoading}
        />

        <Input
          value={lname}
          label="Last name"
          onChange={onLnameChange}
          placeholder="Smith"
          isDisabled={isLoading}
        />

        <ButtonGroup align="space-between" className="flex-row">
          <Button
            iconLeft="arrow-left"
            intent="ghost"
            onClick={() => setLoginState('template-start')}
            type="button"
            value="Back"
          />
          <Button
            value="Continue"
            intent="primary"
            type="submit"
            isDisabled={isLoading || !isValid}
            isLoading={isLoading}
          />
        </ButtonGroup>
      </Flex>
    </form>
  );
};
